import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardOutlined, TeamOutlined, BookOutlined, CalendarOutlined, SolutionOutlined, CheckCircleOutlined, BarChartOutlined, SmileOutlined } from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import { useSettings } from '../context/SettingsContext';

const TeacherLayout = ({ children, user }) => {
    const { t } = useTranslation();
    const { settings } = useSettings();

    // The teacher class and tutor group state should theoretically come from a context or props or API
    // Since we're rendering this Layout we'll try to find this info inside Dashboard later
    // but the main layout just handles standard menus that don't need complex user specific fetches
    // or we'll fetch them in a layout provider context.

    // As a simplification, we'll return the teacher generic menus first. 
    // They can be refined if we need specific access to user.subject_teacher or teacherClass

    const generateTeacherMenu = useCallback((closeMobileDrawer) => {
        const modules = settings?.modules_enabled || {};
        const isKg = settings?.system_template === 'kg';
        const isEnabled = (moduleKey) => modules[moduleKey] !== false;

        const createMenuItem = (key, icon, label, moduleKey) => {
            return {
                key, icon, label: <Link to={key} onClick={closeMobileDrawer}>{label}</Link>
            };
        };

        const createSubMenu = (key, icon, label, children) => {
            if (!children || children.length === 0) return null;
            return { key, icon, label, children };
        };

        const items = [];

        items.push(createMenuItem('/', <DashboardOutlined />, t('common.dashboard'), 'dashboard'));

        const studentItems = [];
        if (isEnabled('students')) studentItems.push(createMenuItem('/students', <TeamOutlined />, t('common.students'), 'students'));
        if (isEnabled('attendance')) studentItems.push(createMenuItem('/attendance', <CheckCircleOutlined />, t('common.attendance'), 'attendance'));
        if (isEnabled('marks')) studentItems.push(createMenuItem('/marks', <BookOutlined />, t('common.marks'), 'marks'));
        items.push(createSubMenu('student-mgmt', <TeamOutlined />, t('menu.studentManagement'), studentItems));

        if (isKg) {
            const kgItems = [
                createMenuItem('/kg/daily-logs', <SmileOutlined />, 'Daily Logs', 'students'),
            ];
            items.push(createSubMenu('kg-mgmt', <SmileOutlined />, 'KG Management', kgItems));
        }

        items.push({ type: 'divider' });

        if (isEnabled('hr')) {
            items.push(createMenuItem('/hr/leaves', <SolutionOutlined />, 'My Leave', 'hr'));
        }

        if (isEnabled('calendar')) items.push(createMenuItem('/calendar', <CalendarOutlined />, t('menu.schoolCalendar'), 'calendar'));
        if (isEnabled('reports')) items.push(createMenuItem('/reports', <BarChartOutlined />, t('menu.reports'), 'reports'));

        // Add library submenu if needed for teacher etc...

        return items;

    }, [settings, t]);

    return (
        <MainLayout customMenuGenerator={generateTeacherMenu}>
            {children}
        </MainLayout>
    );
};

export default TeacherLayout;
